import {
  BULK_EXCLUSION_LABELS,
  EMAIL_STATUS_LABELS,
  type BulkExclusionCode,
  type CampaignDetailDTO,
  type CampaignRecipientDTO,
  type CampaignRecipientFilter,
  type CampaignSummaryDTO,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { OwnerAvatar } from '../../components/crm';
import { Badge, Button, Card, CardBody, EmptyState, Skeleton, Tabs, useConfirm, useToast } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import { formatDateTime, formatNumber } from '../../lib/format';

const STATUS_COLORS: Record<CampaignSummaryDTO['status'], string> = {
  scheduled: 'sky',
  sending: 'amber',
  completed: 'green',
  cancelled: 'gray',
};

/** History of bulk sends: what went out, to how many, and how it performed. */
export function CampaignsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['campaigns', 'list'],
    queryFn: ({ signal }) => api.get<{ items: CampaignSummaryDTO[]; total: number }>('/api/campaigns?pageSize=100', { signal }),
  });
  return (
    <Page width="wide">
      <PageHeader title="Bulk sends" breadcrumbs={[{ label: 'Email', to: '/email' }, { label: 'Bulk sends' }]} description="Start one from the lead table: select pizzerias, then “Send email”." />
      <Card>
        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : (data?.items.length ?? 0) === 0 ? (
          <EmptyState icon={Mail} title="No bulk sends yet" description="Select leads in the lead table and choose “Send email”." action={<Link to="/leads"><Button>Go to leads</Button></Link>} />
        ) : (
          <ul className="divide-y divide-slate-100">
            {data!.items.map((campaign) => (
              <li key={campaign.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-[13px]">
                <Link to={`/email/campaigns/${campaign.id}`} className="font-medium text-slate-900 hover:text-ember-700">
                  {campaign.name}
                </Link>
                <Badge color={STATUS_COLORS[campaign.status]} size="sm">
                  {campaign.status}
                </Badge>
                <span className="text-slate-500">{campaign.selectionDescription}</span>
                <span className="ml-auto text-slate-600">
                  {formatNumber(campaign.counts.sent + campaign.counts.delivered)} sent · {formatNumber(campaign.counts.replied)} replied · {formatNumber(campaign.totalExcluded)} skipped
                </span>
                <OwnerAvatar userId={campaign.createdBy} />
                <span className="text-xs text-slate-400">{formatDateTime(campaign.createdAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Page>
  );
}

const FILTERS: { key: CampaignRecipientFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'queued', label: 'Waiting' },
  { key: 'sent', label: 'Sent' },
  { key: 'replied', label: 'Replied' },
  { key: 'problem', label: 'Problems' },
  { key: 'cancelled', label: 'Cancelled' },
];

export function CampaignDetailPage() {
  const { id = '' } = useParams();
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<CampaignRecipientFilter | 'excluded'>('all');
  const { data } = useQuery({
    queryKey: ['campaigns', id],
    queryFn: ({ signal }) => api.get<CampaignDetailDTO>(`/api/campaigns/${id}`, { signal }),
    refetchInterval: (query) => (query.state.data && query.state.data.remaining > 0 ? 10_000 : false),
  });
  const { data: recipients } = useQuery({
    queryKey: ['campaigns', id, 'recipients', filter],
    queryFn: ({ signal }) => api.get<{ items: CampaignRecipientDTO[]; total: number }>(`/api/campaigns/${id}/recipients?filter=${filter}&pageSize=200`, { signal }),
    enabled: filter !== 'excluded',
  });
  const cancel = useMutation({
    mutationFn: () => api.post<{ cancelled: number }>(`/api/campaigns/${id}/cancel`),
    onSuccess: async (result) => {
      toast.success(`Cancelled ${result.cancelled} unsent email(s)`);
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (!data) return <Page><Skeleton className="h-64 w-full" /></Page>;
  const c = data.counts;

  return (
    <Page width="wide">
      <PageHeader
        title={data.name}
        description={`${data.subject} · ${data.selectionDescription ?? ''}`}
        breadcrumbs={[{ label: 'Email', to: '/email' }, { label: 'Bulk sends', to: '/email/campaigns' }, { label: data.name }]}
        meta={<Badge color={STATUS_COLORS[data.status]}>{data.status}</Badge>}
        actions={
          data.remaining > 0 && !data.cancelledAt ? (
            <Button
              variant="danger"
              onClick={() =>
                void (async () => {
                  if (await confirm({ title: `Cancel ${data.remaining} unsent emails?`, message: 'Emails already sent are not affected.', confirmLabel: 'Cancel remaining', danger: true })) cancel.mutate();
                })()
              }
            >
              Cancel remaining ({data.remaining})
            </Button>
          ) : undefined
        }
      />
      <div className="mb-4 grid gap-3 sm:grid-cols-6">
        {[
          ['Waiting', c.scheduled + c.pending_approval + c.sending],
          ['Sent', c.sent + c.delivered],
          ['Replied', c.replied],
          ['Bounced', c.bounced],
          ['Failed', c.failed + c.uncertain],
          ['Skipped', data.totalExcluded],
        ].map(([label, value]) => (
          <Card key={label}>
            <CardBody>
              <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
              <p className="text-lg font-semibold tabular-nums">{formatNumber(value as number)}</p>
            </CardBody>
          </Card>
        ))}
      </div>
      <Card>
        <Tabs tabs={[...FILTERS, { key: 'excluded', label: 'Skipped' }]} value={filter} onChange={(key) => setFilter(key as CampaignRecipientFilter | 'excluded')} />
        {filter === 'excluded' ? (
          <CardBody>
            <ul className="space-y-1 text-[13px]">
              {(Object.entries(data.exclusionCounts) as [BulkExclusionCode, number][]).map(([code, total]) => (
                <li key={code} className="flex justify-between">
                  <span>{BULK_EXCLUSION_LABELS[code]}</span>
                  <span className="tabular-nums">{total}</span>
                </li>
              ))}
            </ul>
          </CardBody>
        ) : (
          <ul className="divide-y divide-slate-100">
            {(recipients?.items ?? []).map((recipient) => (
              <li key={recipient.emailId} className="flex flex-wrap items-center gap-2 px-4 py-2 text-[13px]">
                {recipient.businessId ? (
                  <Link to={`/leads/${recipient.businessId}?tab=emails`} className="font-medium text-slate-900 hover:text-ember-700">
                    {recipient.businessName}
                  </Link>
                ) : (
                  <span className="font-medium">{recipient.businessName}</span>
                )}
                <span className="text-slate-500">{recipient.contactName ?? ''} {recipient.toEmail}</span>
                <Badge color={recipient.status === 'failed' || recipient.status === 'bounced' ? 'red' : recipient.status === 'sent' || recipient.status === 'delivered' ? 'green' : 'slate'} size="sm">
                  {EMAIL_STATUS_LABELS[recipient.status]}
                </Badge>
                {recipient.repliedAt && <Badge color="violet" size="sm">replied</Badge>}
                {recipient.detail && <span className="text-xs text-slate-500">{recipient.detail}</span>}
              </li>
            ))}
            {recipients && recipients.items.length === 0 && <li className="px-4 py-6 text-center text-[13px] text-slate-500">No recipients in this view.</li>}
          </ul>
        )}
      </Card>
    </Page>
  );
}
